import { useQuery } from '@apollo/client/react';
import * as Linking from 'expo-linking';
import { useFocusEffect, useRouter } from 'expo-router';
import { memo, useCallback, useState } from 'react';
import { FlatList, Platform, StyleSheet, View, type ListRenderItemInfo } from 'react-native';
import { Text } from 'react-native-paper';

import { voiceRecordingRepository } from '@/adapters/database/voiceRecordingRepository';
import { LoadingState } from '@/components/atoms/state';
import { getFileSize } from '@/adapters/storage/fileStorage';
import { VoiceRecordingLocalItem } from '@/components/molecules/voice-recording';
import { ConfirmDialog } from '@/components/atoms/dialog';
import { formatFileSize } from '@/formatters/formatFileSize';
import { MeDocument } from '@/generated/usermobile-bff/graphql';
import { useRepository } from '@/hooks/useRepository';
import { useTranslation } from '@/i18n';
import { type VoiceRecording } from '@/models/voiceRecording';
import { useVoiceRecordingStore } from '@/stores/useVoiceRecordingStore';
import { spacing, unisonColors } from '@/theme';

const flatListContentStyle = { paddingBottom: 80 } as const;

interface VoiceRecordingWithFileSize extends VoiceRecording {
  readonly fileSizeBytes?: number | null;
}

const keyExtractor = (item: VoiceRecordingWithFileSize) => item.id;

const ItemSeparator = () => <View style={styles.itemSeparator} />;

interface LocalRecordingItemWrapperProps {
  readonly recording: VoiceRecordingWithFileSize;
  readonly locale: string;
  readonly deleteLabel: string;
  readonly onDelete: () => void;
  readonly onTurnOnWifi: () => void;
  readonly onChangeUploadSettings: () => void;
  readonly onRetry: () => void;
}

const LocalRecordingItemWrapperBase = ({
  recording,
  locale,
  deleteLabel,
  onDelete,
  onTurnOnWifi,
  onChangeUploadSettings,
  onRetry,
}: LocalRecordingItemWrapperProps) => {
  const fileSizeBytes = recording.fileSizeBytes ?? null;
  const fileSize = typeof fileSizeBytes === 'number' ? formatFileSize(fileSizeBytes) : '';

  return (
    <VoiceRecordingLocalItem
      recordedAt={recording.recordedAt}
      fileSize={fileSize}
      locale={locale}
      uploadStatus={recording.uploadStatus}
      deleteLabel={deleteLabel}
      onDelete={onDelete}
      onTurnOnWifi={onTurnOnWifi}
      onChangeUploadSettings={onChangeUploadSettings}
      onRetry={onRetry}
    />
  );
};

const LocalRecordingItemWrapper = memo(LocalRecordingItemWrapperBase);

const LocalVoiceRecordingsListBase = () => {
  const { t, locale } = useTranslation();
  const router = useRouter();
  const { data, loading } = useQuery(MeDocument);
  const accountId: string | null = data?.me?.accountId ?? null;
  const deleteRecording = useVoiceRecordingStore((state) => state.deleteRecording);
  const retryUpload = useVoiceRecordingStore((state) => state.retryUpload);
  const uploadCompletedAt = useVoiceRecordingStore((state) => state.upload.completedAt);

  const fetchRecordings = useCallback(async () => {
    if (!accountId) {
      return [] as VoiceRecordingWithFileSize[];
    }

    const recordings = await voiceRecordingRepository.findAllByAccountId(accountId);
    const enrichedRecordings = await Promise.all(
      recordings.map(async (recording) => {
        try {
          const fileSizeBytes = await getFileSize(recording.filePath);

          return { ...recording, fileSizeBytes };
        } catch {
          return { ...recording, fileSizeBytes: 0 };
        }
      }),
    );

    return enrichedRecordings;
  }, [accountId]);

  const {
    data: allRecordings,
    isLoading: isLoadingAll,
    refetch,
  } = useRepository<VoiceRecordingWithFileSize>(fetchRecordings, [uploadCompletedAt, accountId]);

  useFocusEffect(
    useCallback(() => {
      void refetch();
    }, [refetch]),
  );

  const deleteLabel = t('recording.actions.delete', { defaultValue: 'Delete input' });
  const deleteConfirmTitle = t('recording.actions.deleteConfirmTitle', {
    defaultValue: 'Delete Input',
  });
  const deleteConfirmMessage = t('recording.actions.deleteConfirmMessage', {
    defaultValue: 'Are you sure you want to delete this input? This action cannot be undone.',
  });
  const cancelLabel = t('common.cancel', { defaultValue: 'Cancel' });

  const handleConfirmDelete = useCallback(
    async (recordingId: string) => {
      const wasDeleted = await deleteRecording(recordingId);
      if (!wasDeleted) {
        return;
      }

      await refetch();
    },
    [deleteRecording, refetch],
  );

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const handleDelete = useCallback((recordingId: string) => {
    setDeleteTargetId(recordingId);
  }, []);

  const handleDismissDelete = useCallback(() => {
    setDeleteTargetId(null);
  }, []);

  const handleConfirmDeleteDialog = useCallback(() => {
    if (deleteTargetId) {
      void handleConfirmDelete(deleteTargetId);
    }
    setDeleteTargetId(null);
  }, [deleteTargetId, handleConfirmDelete]);

  const handleTurnOnWifi = useCallback(() => {
    const action =
      Platform.OS === 'android'
        ? Linking.sendIntent('android.settings.WIFI_SETTINGS')
        : Linking.openURL('App-Prefs:WIFI');

    void action;
  }, []);

  const handleChangeUploadSettings = useCallback(() => {
    router.push({ pathname: '/settings', params: { view: 'network' } });
  }, [router]);

  const handleRetry = useCallback(
    async (recordingId: string) => {
      await retryUpload(recordingId);
      await refetch();
    },
    [refetch, retryUpload],
  );

  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<VoiceRecordingWithFileSize>) => (
      <LocalRecordingItemWrapper
        recording={item}
        locale={locale}
        deleteLabel={deleteLabel}
        onDelete={() => handleDelete(item.id)}
        onTurnOnWifi={handleTurnOnWifi}
        onChangeUploadSettings={handleChangeUploadSettings}
        onRetry={() => void handleRetry(item.id)}
      />
    ),
    [deleteLabel, handleChangeUploadSettings, handleDelete, handleRetry, handleTurnOnWifi, locale],
  );

  if ((loading || isLoadingAll) && allRecordings.length === 0) {
    return <LoadingState />;
  }

  if (allRecordings.length === 0) {
    return (
      <View style={styles.emptyState}>
        <Text style={styles.emptyText}>
          {t('settings.storage.emptyMessage', {
            defaultValue: 'No local memo inputs to display',
          })}
        </Text>
      </View>
    );
  }

  return (
    <>
      <FlatList
        data={allRecordings}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        ItemSeparatorComponent={ItemSeparator}
        contentContainerStyle={flatListContentStyle}
      />
      <ConfirmDialog
        visible={deleteTargetId !== null}
        title={deleteConfirmTitle}
        body={deleteConfirmMessage}
        confirmLabel={deleteLabel}
        dismissLabel={cancelLabel}
        onConfirm={handleConfirmDeleteDialog}
        onDismiss={handleDismissDelete}
      />
    </>
  );
};

export const LocalVoiceRecordingsList = memo(LocalVoiceRecordingsListBase);

const styles = StyleSheet.create({
  emptyState: {
    borderTopWidth: 1,
    borderTopColor: unisonColors.borderNdBase,
    paddingVertical: spacing[4],
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 24,
    color: unisonColors.textNdSecondary,
  },
  itemSeparator: {
    height: spacing[2],
  },
});
